import { useState, memo, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useForm, useFormState, useWatch, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Link } from 'react-router-dom';
import { useHistory } from 'react-router-dom';

import FormInput from 'components/FormInput';
import DashboardContainer from 'components/DashboardContainer';
import CountrySelect from 'components/CountrySelect';
import MerchantCategorySelect from 'components/MerchantCategorySelect';
import ClientSelectDropdown from 'components/ClientSelectDropdown';
import FeeStructureSelect from 'components/FeeStructureSelect';
import IntegrationSelect from 'components/IntegrationSelect';
import Checkbox from 'components/Checkbox';
import PATHS from 'routes/paths';
import { CompanyNew as TEXT } from 'locales/en';
import schema, { FIELD_NAMES } from './schema';
import ImageUpload from 'components/ImageUpload';
import { createCompany } from 'redux/slices/company';
import Button from 'components/Button';
import SelectInput from 'components/SelectInput';

const CompanyNew = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [checkoutLogoPath, setCheckoutLogoPath] = useState(null);

  const { register, handleSubmit, control, setValue, trigger } = useForm({
    resolver: yupResolver(schema),
  });

  const { errors, isSubmitting } = useFormState({
    control,
  });

  const [checkout_logo] = useWatch({
    control,
    name: [FIELD_NAMES.checkout_logo],
  });

  const onSubmit = async (data) => {
    return dispatch(
      createCompany({
        ...data,
        checkout_logo: checkoutLogoPath,
        legal_entity: data[FIELD_NAMES.legal_entity].value,
        country: data[FIELD_NAMES.country].code,
        mcc_code: data[FIELD_NAMES.mcc_code].value,
        client_id: data[FIELD_NAMES.client_id].value,
        fee_structure_id: data[FIELD_NAMES.fee_structure_id].value,
        integration_id: data[FIELD_NAMES.integration_id].value,
      })
    ).then((action) => {
      if (action.type.endsWith('fulfilled')) {
        history.push(PATHS.companies);
      }
    });
  };

  const checkoutLogoPlaceholder = useMemo(() => {
    return checkout_logo?.name ?? '';
  }, [checkout_logo]);

  return (
    <DashboardContainer header={TEXT.header}>
      <form noValidate onSubmit={handleSubmit(onSubmit)} className="w-full">
        <div className="w-full rounded-lg bg-white shadow-md">
          <div className="mr-4 w-full p-12 lg:w-7/12">
            <FormInput
              label={`${TEXT.display_name} *`}
              name={FIELD_NAMES.display_name}
              register={register}
              required
              error={errors[FIELD_NAMES.display_name]?.message}
            />
            <FormInput
              label={`${TEXT.legal_business_name} *`}
              name={FIELD_NAMES.legal_business_name}
              register={register}
              required
              error={errors[FIELD_NAMES.legal_business_name]?.message}
            />
            <FormInput
              label={`${TEXT.first_name} *`}
              name={FIELD_NAMES.first_name}
              register={register}
              required
              error={errors[FIELD_NAMES.first_name]?.message}
            />
            <FormInput
              label={`${TEXT.last_name} *`}
              name={FIELD_NAMES.last_name}
              register={register}
              required
              error={errors[FIELD_NAMES.last_name]?.message}
            />
            <FormInput
              label={`${TEXT.email} *`}
              name={FIELD_NAMES.email}
              register={register}
              error={errors[FIELD_NAMES.email]?.message}
            />
            <FormInput
              label={`${TEXT.phone_number} *`}
              name={FIELD_NAMES.phone_number}
              register={register}
              error={errors[FIELD_NAMES.phone_number]?.message}
            />
            <CountrySelect
              label={`${TEXT.country} *`}
              name={FIELD_NAMES.country}
              control={control}
              inputBelowLabel={false}
              error={errors[FIELD_NAMES.country]?.name?.message}
            />

            <Controller
              control={control}
              name={FIELD_NAMES.legal_entity}
              render={({ field: { ref, value, ..._field } }) => (
                <SelectInput
                  {..._field}
                  label={`${TEXT.legal_entity} *`}
                  name={FIELD_NAMES.legal_entity}
                  options={TEXT.legal_entity_options}
                  required
                  inputBelowLabel={false}
                  error={errors[FIELD_NAMES.legal_entity]?.label?.message}
                />
              )}
            />
          </div>
          <hr />
          <div className="mr-4 w-full p-12 lg:w-7/12">
            <MerchantCategorySelect
              label={TEXT.mcc_code}
              name={FIELD_NAMES.mcc_code}
              control={control}
              inputBelowLabel={false}
              error={errors[FIELD_NAMES.mcc_code]?.label?.message}
            />
            <FormInput
              label={TEXT.google_pay_merchant_id}
              name={FIELD_NAMES.google_pay_merchant_id}
              register={register}
              error={errors[FIELD_NAMES.google_pay_merchant_id]?.message}
            />
            <ImageUpload
              control={control}
              trigger={trigger}
              setValue={setValue}
              name={FIELD_NAMES.checkout_logo}
              label={TEXT.checkout_logo}
              error={errors[FIELD_NAMES.checkout_logo]?.message}
              placeholder={checkoutLogoPlaceholder}
              setImagePath={setCheckoutLogoPath}
            />
          </div>
          <hr />
          <div className="mr-4 w-full p-12 lg:w-7/12">
            <FormInput
              label={TEXT.shortcode}
              name={FIELD_NAMES.shortcode}
              register={register}
              error={errors[FIELD_NAMES.shortcode]?.message}
            />
            <FormInput
              label={TEXT.account_holder_code}
              name={FIELD_NAMES.account_holder_code}
              register={register}
              error={errors[FIELD_NAMES.account_holder_code]?.message}
            />
            <FormInput
              label={TEXT.stripe_account_id}
              name={FIELD_NAMES.stripe_account_id}
              register={register}
              error={errors[FIELD_NAMES.stripe_account_id]?.message}
            />
          </div>
          <hr />
          <div className="mr-4 w-full p-12 lg:w-7/12">
            <ClientSelectDropdown
              label={`${TEXT.client_id} *`}
              name={FIELD_NAMES.client_id}
              control={control}
              required
              inputBelowLabel={false}
              error={errors[FIELD_NAMES.client_id]?.label?.message}
            />
            <FeeStructureSelect
              label={TEXT.fee_structure_id}
              name={FIELD_NAMES.fee_structure_id}
              control={control}
              required
              inputBelowLabel={false}
              error={errors[FIELD_NAMES.fee_structure_id]?.message}
            />
            <IntegrationSelect
              label={TEXT.integration_id}
              name={FIELD_NAMES.integration_id}
              control={control}
              required
              inputBelowLabel={false}
              error={errors[FIELD_NAMES.integration_id]?.message}
            />
            <label
              htmlFor={FIELD_NAMES.caboodle_fleet}
              className="mb-4 flex flex-1 flex-col lg:flex-row lg:items-center "
            >
              <div className="label pb-1 lg:w-4/12 lg:pb-0">
                {TEXT.caboodle_fleet}
              </div>
              <div className="input-form-wrapper ">
                <div className="relative">
                  <Checkbox name={FIELD_NAMES.caboodle_fleet} />
                </div>
              </div>
            </label>
          </div>
        </div>
        <div className="mt-8 flex justify-end">
          <Link to={PATHS.companies}>
            <button
              type="button"
              className="btn-secondary mr-6 rounded-full px-12 py-3 text-sm"
            >
              {TEXT.cancel}
            </button>
          </Link>
          <Button
            className="btn-primary block h-12 w-56 px-0 lg:w-48"
            type="submit"
            title={TEXT.header}
            showSpinner={isSubmitting}
            isDisabled={isSubmitting}
          />
        </div>
      </form>
    </DashboardContainer>
  );
};

export default memo(CompanyNew);
